// İSG uzmanı portalı — Kullanıcı yönetimi (api-contract §12, tenant-scoped). Firma
// kullanıcı listesi (UTable sözleşmesi: page/pageSize/search + total/filteredTotal),
// ekle/düzenle (UserFormModal), soft-sil/pasifleştir (DELETE /users/{uuid}). **Kendini
// silme** UI'da engellenir (buton gizli — 409 CANNOT_DELETE_SELF önlenir); kendini
// pasifleştirme düzenle formunda engellenir.

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Pagination,
  type RowAction,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  type UserDto,
  resolveApiErrorMessage,
  usersApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, SearchX, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserFormModal } from '../../components/users/UserFormModal';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { useMe } from '../../lib/useMe';
import { usersKeys } from '../../lib/usersKeys';

const PAGE_SIZE = 20;

export function UsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { me } = useMe();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserDto | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserDto | null>(null);

  // Arama değişince ilk sayfaya dön (efekt yerine kaynağında).
  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params = { page, pageSize: PAGE_SIZE, search: debouncedSearch };

  const query = useQuery({
    queryKey: usersKeys.listParams(params),
    queryFn: () => usersApi.listUsers(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => usersApi.deleteUser(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.list() });
      setMessage(t('users.deleted'));
      setDeleteUser(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  function fullName(u: UserDto): string {
    return `${u.firstName} ${u.lastName}`.trim();
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('users.title') }]} />
      <PageHeader
        title={t('users.title')}
        helperText={t('users.subtitle')}
        actions={
          <Button
            onClick={() => {
              setMessage(null);
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('users.new')}
          </Button>
        }
      />

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={query.data ? t('users.count', { count: query.data.filteredTotal }) : undefined}
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('users.common.search')}
            searchPlaceholder={t('users.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('users.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.col.name')}</TableHead>
                <TableHead>{t('users.col.email')}</TableHead>
                <TableHead>{t('users.col.role')}</TableHead>
                <TableHead>{t('users.col.status')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('users.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('users.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Users}
                      title={t('users.common.empty')}
                      description={t('users.common.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('users.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((row) => {
                  const isSelf = me != null && row.uuid === me.uuid;
                  // Kendini silme engeli — kendi satırında sil ögesi menüde HİÇ görünmez (409
                  // CANNOT_DELETE_SELF önlenir; §12) — eski hale birebir aynı (gizli, disabled
                  // DEĞİL).
                  const rowActions: RowAction[] = isSelf
                    ? []
                    : [
                        {
                          key: 'delete',
                          label: t('users.action.delete'),
                          destructive: true,
                          onSelect: () => {
                            setMessage(null);
                            setDeleteUser(row);
                          },
                        },
                      ];
                  return (
                    <TableRow key={row.uuid}>
                      <TableCell>
                        {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — ayrı bir
                            görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                        <button
                          type="button"
                          onClick={() => {
                            setMessage(null);
                            setEditUser(row);
                          }}
                          className="text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {fullName(row)}
                        </button>
                      </TableCell>
                      <TableCell className="text-muted">{row.email}</TableCell>
                      <TableCell className="text-muted">{row.roleName ?? '—'}</TableCell>
                      <TableCell>
                        <Tag color={row.isActive ? 'green' : 'gray'}>
                          {row.isActive ? t('users.status.active') : t('users.status.passive')}
                        </Tag>
                      </TableCell>
                      <TableCell className="w-12 px-2">
                        {rowActions.length > 0 ? (
                          <RowActionsMenu
                            ariaLabel={t('users.action.openMenu', { name: fullName(row) })}
                            items={rowActions}
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('users.pagination.nav'),
                previous: t('users.pagination.previous'),
                next: t('users.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <UserFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setMessage(t('users.created'))}
      />
      <UserFormModal
        open={editUser !== null}
        onOpenChange={(open) => !open && setEditUser(null)}
        user={editUser}
        onSuccess={() => setMessage(t('users.updated'))}
      />
      <ConfirmDialog
        open={deleteUser !== null}
        onOpenChange={(open) => !open && setDeleteUser(null)}
        title={t('users.delete.title')}
        body={t('users.delete.body', { name: deleteUser ? fullName(deleteUser) : '' })}
        confirmLabel={t('users.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteUser && deleteMutation.mutate(deleteUser.uuid)}
      />
    </div>
  );
}
